import React, { useState, useCallback, useRef, createContext, useContext } from 'react';
import BaseDialog from '../BaseDialog/BaseDialog';
import styles from './ConfirmationDialog.module.css';

/**
 * @typedef {Object} ConfirmationDialogCustomProps
 * @property {boolean} [open] - Controls the visibility of the dialog.
 * @property {() => void} [onClose] - Callback function for when the dialog is requested to close (backdrop, escape, close icon).
 * @property {() => void} [onCancel] - Callback function for when the "Cancel" button is clicked.
 * @property {() => void} [onConfirm] - Callback function for when the "Confirm" button is clicked.
 * @property {() => void} [onAccept] - Legacy support for onConfirm prop.
 * @property {string} [title] - The title text displayed at the top of the dialog.
 * @property {string|string[]} [text] - The main text content displayed in the dialog body.
 * @property {string|string[]} [error] - Legacy support for text prop.
 * @property {string} [confirmLabel] - The text label for the "Confirm" button.
 * @property {string} [acceptTitle] - Legacy support for confirmLabel prop.
 * @property {string} [AcceptBtnTitle] - Legacy support for confirmLabel prop.
 * @property {string} [cancelLabel] - The text label for the "Cancel" button.
 * @property {string} [cancelTitle] - Legacy support for cancelLabel prop.
 * @property {string} [cancelBtnTitle] - Legacy support for cancelLabel prop.
 * @property {string} [dialogType] - Type of dialog (legacy prop, unused).
 * @property {boolean} [hideCancle] - Backward compatibility prop for hiding the cancel button.
 * @property {boolean} [hideCancel] - Corrected prop for hiding the cancel button.
 * @property {string} [color] - The theme color for the dialog buttons and title. Defaults to '#4b1c46'.
 * @property {boolean} [loading] - If true, shows a loading spinner and disables the confirm button.
 * @property {boolean} [disabled] - If true, disables all action buttons.
 * @property {React.ReactNode} [children] - Custom content to render below the main text.
 * @property {string} [className] - Optional custom class name for the root element.
 * @property {'left'|'right'|'center'} [align] - Text alignment for the dialog content. Defaults to 'center'.
 *
 * @typedef {import('react').DialogHTMLAttributes<HTMLDialogElement> & ConfirmationDialogCustomProps} ConfirmationDialogProps
 */

/**
 * A modal dialog component for displaying confirmation messages and capturing user decisions.
 * Refactored to inherit core modal styles and native handlers from BaseDialog.
 * 
 * @param {ConfirmationDialogProps} props
 * @returns {React.ReactElement}
 */
const ConfirmationDialog = ({
  open,
  onClose,
  onCancel,
  onConfirm,
  onAccept, // legacy
  title = 'Confirmation',
  text = 'Are you sure you want to proceed?',
  error, // legacy
  confirmLabel = 'Yes',
  acceptTitle, // legacy
  AcceptBtnTitle, // legacy
  cancelLabel = 'Cancel',
  cancelTitle, // legacy
  cancelBtnTitle, // legacy
  dialogType = 'info', // Kept for legacy support
  hideCancle = false, // Backward compatibility
  hideCancel = false, // New corrected prop
  color = '#4b1c46',  // Default theme color
  loading = false,    // Loading state
  disabled = false,   // Disabled state
  children = null,    // Custom content
  className = '',
  align = 'left',
  ...rest
}) => {
  const isOpen = open;
  const displayText = error !== undefined ? error : text;
  const displayConfirmLabel = acceptTitle || AcceptBtnTitle || confirmLabel;
  const displayCancelLabel = cancelTitle || cancelBtnTitle || cancelLabel;
  const handleConfirm = onConfirm || onAccept;
  const handleCancelAction = onCancel || onClose;

  const shouldHideCancel = hideCancel || hideCancle || displayCancelLabel === null;

  const renderContent = () => {
    if (Array.isArray(displayText)) {
      return (
        <ul className={`${styles.contentText} confirmation-dialog-list`} style={{ paddingLeft: '20px', margin: 0, textAlign: align }}>
          {displayText.map((item, index) => (
            <li key={index} className="confirmation-dialog-list-item">{item}</li>
          ))}
        </ul>
      );
    }
    return <p className={`${styles.contentText} confirmation-dialog-text`} style={{ textAlign: align }}>{displayText}</p>;
  };

  return (
    <BaseDialog
      open={isOpen}
      onClose={handleCancelAction}
      onCancel={handleCancelAction}
      onConfirm={handleConfirm}
      title={title}
      confirmLabel={displayConfirmLabel}
      cancelLabel={displayCancelLabel}
      hideCancel={shouldHideCancel}
      color={color}
      loading={loading}
      disabled={disabled}
      centerText={true} // Dynamic text centering matching standard prompts
      className={`${className} confirmation-dialog-root`}
      {...rest}
    >
      {renderContent()}
      {children}
    </BaseDialog>
  );
};

const ConfirmationContext = createContext(null);

export const ConfirmationDialogProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dialogProps, setDialogProps] = useState({});
  const resolveRef = useRef(null);

  const confirm = useCallback((customProps = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setDialogProps(customProps);
      setIsOpen(true);
    });
  }, []);

  const handleClose = useCallback((result) => {
    setIsOpen(false);
    resolveRef.current?.(result);
    resolveRef.current = null;
  }, []);

  const {
    title = 'Confirm?',
    bodyMessage = 'Are you sure you want to proceed?',
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    ...restProps
  } = dialogProps;

  return (
    <ConfirmationContext.Provider value={confirm}>
      {children}
      <ConfirmationDialog
        open={isOpen}
        onConfirm={() => handleClose(true)}
        onCancel={() => handleClose(false)}
        onClose={() => handleClose(false)}
        title={title}
        text={bodyMessage}
        confirmLabel={confirmText}
        cancelLabel={cancelText}
        {...restProps}
      />
    </ConfirmationContext.Provider>
  );
};

export const useConfirmation = () => {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error('useConfirmation must be used within a ConfirmationDialogProvider');
  }
  return context;
};

export const useConfirmationDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const resolveRef = useRef();
  const [dialogProps, setDialogProps] = useState({});

  const showDialog = useCallback((customProps = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setDialogProps(customProps);
      setIsOpen(true);
    });
  }, []);

  const handleClose = useCallback((result) => {
    setIsOpen(false);
    resolveRef.current?.(result);
  }, []);

  const DialogComponent = useCallback(
    () => {
      const {
        title = 'Confirm?',
        bodyMessage = 'Are you sure you want to proceed?',
        confirmText = 'Confirm',
        cancelText = 'Cancel',
        ...restProps
      } = dialogProps;

      return (
        <ConfirmationDialog
          open={isOpen}
          onConfirm={() => handleClose(true)}
          onCancel={() => handleClose(false)}
          onClose={() => handleClose(false)}
          title={title}
          text={bodyMessage}
          confirmLabel={confirmText}
          cancelLabel={cancelText}
          {...restProps}
        />
      );
    },
    [isOpen, handleClose, dialogProps]
  );

  return [showDialog, DialogComponent];
};

export default ConfirmationDialog;
